/**
 * How many documents each claim is still waiting on, for the claims list.
 *
 * The customer API answers with counts keyed by application id, plus an
 * `available` flag. That flag matters more than it looks: when the admin API
 * cannot be reached we are told so, rather than being handed zeros. Showing a
 * citizen "nothing outstanding" when we do not actually know is the one
 * genuinely harmful outcome here, because they would read it as confirmation
 * and miss a real request. Every function below treats unavailable as "say
 * nothing at all".
 *
 * JSON objects cannot have integer keys, so the ids arrive as strings while a
 * claim row carries a number. Lookups go through `countFor`, which normalises.
 */

export interface OpenCounts {
  counts: Record<string, number>;
  total: number;
  claims: number;
  available: boolean;
}

export const NOTHING_KNOWN: OpenCounts = {
  counts: {},
  total: 0,
  claims: 0,
  available: false,
};

/** Coerce whatever the API returned into a shape the page can trust. */
export function parseOpenCounts(payload: unknown): OpenCounts {
  const body = payload as Partial<OpenCounts> | null | undefined;
  if (!body || typeof body !== "object" || body.available !== true) {
    return NOTHING_KNOWN;
  }
  const counts =
    body.counts && typeof body.counts === "object" ? body.counts : {};
  return {
    counts: counts as Record<string, number>,
    total: Number(body.total) || 0,
    claims: Number(body.claims) || 0,
    available: true,
  };
}

/** Documents outstanding on one claim. Zero when we were not told. */
export function countFor(open: OpenCounts, claimId: unknown): number {
  if (!open.available) return 0;
  if (claimId === null || claimId === undefined) return 0;
  const value = open.counts[String(claimId)];
  return typeof value === "number" && value > 0 ? value : 0;
}

export function needsAction(open: OpenCounts, claimId: unknown): boolean {
  return countFor(open, claimId) > 0;
}

/** The badge text. Plural handled, because "1 documents" reads as a bug. */
export function actionLabel(count: number): string {
  if (count <= 0) return "";
  return count === 1 ? "1 document needed" : `${count} documents needed`;
}

/** What the row's link should say. */
export function actionLinkText(open: OpenCounts, claimId: unknown): string {
  return needsAction(open, claimId) ? "Upload documents" : "View";
}

/**
 * The line above the table. Unlike the cards equivalent this covers EVERY claim
 * the citizen has, not just the page being shown, because the API counts them
 * all. So it is worded as a total and says nothing when there is nothing to say.
 */
export function summary(open: OpenCounts): string {
  if (!open.available || open.claims <= 0) return "";
  const claims =
    open.claims === 1 ? "1 claim needs" : `${open.claims} claims need`;
  const docs =
    open.total === 1 ? "a document" : `${open.total} documents`;
  return `${claims} ${docs} from you.`;
}
